import React, { useState, useEffect } from 'react';
import { 
  Save, Edit, Eye, Plus, Trash2, X, Upload, Image, Users, Target, Award, CheckCircle,
  Globe, Zap, Code, Smartphone, Palette, Database, Cloud, Shield, Settings, Mail, Phone, MapPin,
  ShoppingBag, Star, Download, Headphones, RefreshCw, BookOpen, AlertCircle, MessageCircle, Clock,
  Calendar, Bell, ArrowRight, ArrowLeft, DollarSign, Send, ShoppingCart, Briefcase, Linkedin, Twitter, Facebook, Instagram, Youtube, Github
} from 'lucide-react';
import { contactPageAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';

const ContactPageManagement = () => {
  const { notify } = useNotificationHook();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [contactData, setContactData] = useState({
    // Top Section
    main_title: 'Get In',
    gradient_title: 'Touch',
    description: 'Ready to start your project? Let\'s discuss how we can help bring your vision to life.',
    
    // Contact Options (replacing single call section)
    contact_options: [
      {
        id: 1,
        type: 'call',
        icon: 'Phone',
        icon_background: 'from-green-600 to-green-700',
        title: 'Call Us',
        description: 'Speak directly with our team',
        value: '9876543210',
        link: 'tel:+919876543210'
      },
      {
        id: 2,
        type: 'email',
        icon: 'Mail',
        icon_background: 'from-blue-600 to-blue-700',
        title: 'Email Us',
        description: 'Send us a detailed message',
        value: 'info@niswainfotech.com',
        link: 'mailto:info@niswainfotech.com'
      },
      {
        id: 3,
        type: 'whatsapp',
        icon: 'MessageCircle',
        icon_background: 'from-green-500 to-green-600',
        title: 'WhatsApp',
        description: 'Quick chat on WhatsApp',
        value: '+91 9876543210',
        link: 'https://wa.me/919876543210'
      },
      {
        id: 4,
        type: 'schedule',
        icon: 'Calendar',
        icon_background: 'from-purple-600 to-purple-700',
        title: 'Schedule Meeting',
        description: 'Book a consultation call',
        value: 'Book Now',
        link: '/schedule'
      }
    ],
    
    // Form Section
    form_title: 'Send Us a Message',
    form_description: 'Fill out the form below and we\'ll get back to you within 24 hours.',
    
    // Other Contact Methods
    other_contact_title: 'Other Ways to Reach Us',
    other_contact_options: [
      {
        id: 1,
        icon: 'Phone',
        title: 'Call Us',
        description: 'Give us a call',
        value: '+91 9876543210'
      },
      {
        id: 2,
        icon: 'Mail',
        title: 'Email Us',
        description: 'Drop us an email',
        value: 'info@niswainfotech.com'
      },
      {
        id: 3,
        icon: 'MessageCircle',
        title: 'WhatsApp',
        description: 'Quick chat on WhatsApp',
        value: '+91 9876543210'
      }
    ],
    phone_icon: 'Phone',
    phone_subtitle: 'Phone',
    phone_value: '9876543210',
    
    // Office Address
    office_title: 'Visit Our Office',
    office_items: [
      {
        id: 1,
        type: 'address',
        icon: 'MapPin',
        title: 'Address',
        value: '123 Business District, Andheri East, Mumbai, Maharashtra 400069',
        link: ''
      },
      {
        id: 2,
        type: 'hours',
        icon: 'Clock',
        title: 'Business Hours',
        value: 'Monday - Friday: 9:00 AM - 6:00 PM\nSaturday: 10:00 AM - 4:00 PM\nSunday: Closed',
        link: ''
      },
      {
        id: 3,
        type: 'email',
        icon: 'Mail',
        title: 'Email',
        value: 'info@niswainfotech.com',
        link: 'mailto:info@niswainfotech.com'
      },
      {
        id: 4,
        type: 'phone',
        icon: 'Phone',
        title: 'Phone',
        value: '9876543210',
        link: 'tel:+919876543210'
      }
    ],
    
    // Map Section
    map_title: 'Interactive Map',
    map_address: 'Mumbai, Maharashtra',
    google_maps_api_key: '',
    map_phone: '+91 98765 43210',
    map_email: 'hello@niswainfotech.com',
    
    // Social Media Section
    social_title: 'Follow Us',
    social_links: [
      { name: 'Facebook', icon: 'Facebook', url: 'https://facebook.com/niswainfotech', color: 'hover:text-blue-600' },
      { name: 'Twitter', icon: 'Twitter', url: 'https://twitter.com/niswainfotech', color: 'hover:text-blue-400' },
      { name: 'Instagram', icon: 'Instagram', url: 'https://instagram.com/niswainfotech', color: 'hover:text-pink-600' },
      { name: 'LinkedIn', icon: 'Linkedin', url: 'https://linkedin.com/company/niswainfotech', color: 'hover:text-blue-700' },
      { name: 'YouTube', icon: 'Youtube', url: 'https://youtube.com/niswainfotech', color: 'hover:text-red-600' }
    ],
    
    // FAQ Section
    faq_title: 'Frequently Asked Questions',
    faq_description: 'Quick answers to common questions about our services and process',
    faq_items: [
      {
        question: 'How long does a typical project take?',
        answer: 'Project timelines vary based on complexity. Simple websites take 2-4 weeks, while complex applications can take 8-16 weeks. We provide detailed timelines during consultation.'
      },
      {
        question: 'Do you provide ongoing support and maintenance?',
        answer: 'Yes, we offer comprehensive support packages including regular updates, security monitoring, performance optimization, and technical support.'
      },
      {
        question: 'What is your payment structure?',
        answer: 'We typically work with a 50% upfront payment and 50% upon completion. For larger projects, we can arrange milestone-based payments.'
      },
      {
        question: 'Can you work with our existing team?',
        answer: 'Absolutely! We collaborate seamlessly with in-house teams and can integrate with your existing workflows and tools.'
      }
    ]
  });

  const [activeSection, setActiveSection] = useState('top-section');

  // Available icons for selection
  const availableIcons = [
    { name: 'Users', component: Users },
    { name: 'Zap', component: Zap },
    { name: 'Headphones', component: Headphones },
    { name: 'Globe', component: Globe },
    { name: 'Mail', component: Mail },
    { name: 'Bell', component: Bell },
    { name: 'Star', component: Star },
    { name: 'Shield', component: Shield },
    { name: 'Cloud', component: Cloud },
    { name: 'Code', component: Code },
    { name: 'Palette', component: Palette },
    { name: 'Database', component: Database },
    { name: 'Smartphone', component: Smartphone },
    { name: 'ShoppingCart', component: ShoppingCart },
    { name: 'ArrowRight', component: ArrowRight },
    { name: 'ArrowLeft', component: ArrowLeft },
    { name: 'CheckCircle', component: CheckCircle },
    { name: 'Settings', component: Settings },
    { name: 'DollarSign', component: DollarSign },
    { name: 'Download', component: Download },
    { name: 'Send', component: Send },
    { name: 'MessageCircle', component: MessageCircle },
    { name: 'Clock', component: Clock },
    { name: 'Calendar', component: Calendar },
    { name: 'Phone', component: Phone },
    { name: 'MapPin', component: MapPin },
    { name: 'Briefcase', component: Briefcase },
    { name: 'Linkedin', component: Linkedin },
    { name: 'Twitter', component: Twitter },
    { name: 'Facebook', component: Facebook },
    { name: 'Instagram', component: Instagram },
    { name: 'Youtube', component: Youtube },
    { name: 'Github', component: Github }
  ];

  const sections = [
    { id: 'top-section', label: 'Top Section', icon: Star },
    { id: 'contacts-section', label: 'Contacts', icon: Phone },
    { id: 'form-section', label: 'Form Section', icon: MessageCircle },
    { id: 'other-contact-section', label: 'Other Ways to Reach Us', icon: Headphones },
    { id: 'office-section', label: 'Office Address', icon: MapPin },
    { id: 'map-section', label: 'Map Section', icon: Globe },
    { id: 'social-section', label: 'Social Media', icon: Users },
    { id: 'faq-section', label: 'FAQ Section', icon: AlertCircle }
  ];

  // Fetch contact page data
  useEffect(() => {
    fetchContactData();
  }, []);

  const fetchContactData = async () => {
    try {
      setLoading(true);
      const response = await contactPageAPI.getAdmin();
      if (response?.data?.success) {
        const data = response.data.data;
                setContactData({
          main_title: data.main_title || 'Get In',
          gradient_title: data.gradient_title || 'Touch',
          description: data.description || 'Ready to start your project? Let\'s discuss how we can help bring your vision to life.',
          contact_options: data.contact_options || [
            {
              id: 1,
              type: 'call',
              icon: 'Phone',
              icon_background: 'from-green-600 to-green-700',
              title: 'Call Us',
              description: 'Speak directly with our team',
              value: '9876543210',
              link: 'tel:+919876543210'
            },
            {
              id: 2,
              type: 'email',
              icon: 'Mail',
              icon_background: 'from-blue-600 to-blue-700',
              title: 'Email Us',
              description: 'Send us a detailed message',
              value: 'info@niswainfotech.com',
              link: 'mailto:info@niswainfotech.com'
            },
            {
              id: 3,
              type: 'whatsapp',
              icon: 'MessageCircle',
              icon_background: 'from-green-500 to-green-600',
              title: 'WhatsApp',
              description: 'Quick chat on WhatsApp',
              value: '+91 9876543210',
              link: 'https://wa.me/919876543210'
            },
            {
              id: 4,
              type: 'schedule',
              icon: 'Calendar',
              icon_background: 'from-purple-600 to-purple-700',
              title: 'Schedule Meeting',
              description: 'Book a consultation call',
              value: 'Book Now',
              link: '/schedule'
            }
          ],
          form_title: data.form_title || 'Send Us a Message',
          form_description: data.form_description || 'Fill out the form below and we\'ll get back to you within 24 hours.',
          other_contact_title: data.other_contact_title || 'Other Ways to Reach Us',
          other_contact_options: data.other_contact_options || [
            {
              id: 1,
              icon: 'Phone',
              title: 'Call Us',
              description: 'Give us a call',
              value: '+91 9876543210'
            },
            {
              id: 2,
              icon: 'Mail',
              title: 'Email Us',
              description: 'Drop us an email',
              value: 'info@niswainfotech.com'
            },
            {
              id: 3,
              icon: 'MessageCircle',
              title: 'WhatsApp',
              description: 'Quick chat on WhatsApp',
              value: '+91 9876543210'
            }
          ],
          phone_icon: data.phone_icon || 'Phone',
          phone_subtitle: data.phone_subtitle || 'Phone',
          phone_value: data.phone_value || '9876543210',
          office_title: data.office_title || 'Visit Our Office',
          office_items: data.office_items || [
            {
              id: 1,
              type: 'address',
              icon: 'MapPin',
              title: 'Address',
              value: '123 Business District, Andheri East, Mumbai, Maharashtra 400069',
              link: ''
            },
            {
              id: 2,
              type: 'hours',
              icon: 'Clock',
              title: 'Business Hours',
              value: 'Monday - Friday: 9:00 AM - 6:00 PM\nSaturday: 10:00 AM - 4:00 PM\nSunday: Closed',
              link: ''
            },
            {
              id: 3,
              type: 'email',
              icon: 'Mail',
              title: 'Email',
              value: 'info@niswainfotech.com',
              link: 'mailto:info@niswainfotech.com'
            },
            {
              id: 4,
              type: 'phone',
              icon: 'Phone',
              title: 'Phone',
              value: '9876543210',
              link: 'tel:+919876543210'
            }
          ],
          map_title: data.map_title || 'Interactive Map',
          map_address: data.map_address || 'Mumbai, Maharashtra',
          google_maps_api_key: data.google_maps_api_key || '',
          map_phone: data.map_phone || '+91 98765 43210',
          map_email: data.map_email || 'hello@niswainfotech.com',
          social_title: data.social_title || 'Follow Us',
          social_links: data.social_links || [
            { name: 'Facebook', icon: 'Facebook', url: 'https://facebook.com/niswainfotech', color: 'hover:text-blue-600' },
            { name: 'Twitter', icon: 'Twitter', url: 'https://twitter.com/niswainfotech', color: 'hover:text-blue-400' },
            { name: 'Instagram', icon: 'Instagram', url: 'https://instagram.com/niswainfotech', color: 'hover:text-pink-600' },
            { name: 'LinkedIn', icon: 'Linkedin', url: 'https://linkedin.com/company/niswainfotech', color: 'hover:text-blue-700' },
            { name: 'YouTube', icon: 'Youtube', url: 'https://youtube.com/niswainfotech', color: 'hover:text-red-600' }
          ],
          faq_title: data.faq_title || 'Frequently Asked Questions',
          faq_description: data.faq_description || 'Quick answers to common questions about our services and process',
          faq_items: data.faq_items || [
            {
              question: 'How long does a typical project take?',
              answer: 'Project timelines vary based on complexity. Simple websites take 2-4 weeks, while complex applications can take 8-16 weeks. We provide detailed timelines during consultation.'
            },
            {
              question: 'Do you provide ongoing support and maintenance?',
              answer: 'Yes, we offer comprehensive support packages including regular updates, security monitoring, performance optimization, and technical support.'
            },
            {
              question: 'What is your payment structure?',
              answer: 'We typically work with a 50% upfront payment and 50% upon completion. For larger projects, we can arrange milestone-based payments.'
            },
            {
              question: 'Can you work with our existing team?',
              answer: 'Absolutely! We collaborate seamlessly with in-house teams and can integrate with your existing workflows and tools.'
            }
          ]
        });
      }
    } catch (err) {
      console.error('Error fetching contact data:', err);
      setError(`Error loading contact data: ${err.message}`);
      notify.error(`Error loading contact data: ${err.message}`, 'Loading Error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setContactData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveContactData = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      await contactPageAPI.updateContent(contactData);
      setSuccess('Contact page content updated successfully!');
      notify.success('Contact page content updated successfully!', 'Success');
    } catch (err) {
      console.error('Error saving contact data:', err);
      setError(`Error saving contact data: ${err.message}`);
      notify.error(`Error saving contact data: ${err.message}`, 'Save Error');
    } finally {
      setSaving(false);
    }
  };

  const clearMessage = () => {
    setError(null);
    setSuccess(null);
  };

  const getIconComponent = (iconName) => {
    const icon = availableIcons.find(i => i.name === iconName);
    return icon ? icon.component : Users;
  };

  const handleSocialLinkChange = (index, field, value) => {
    setContactData(prev => ({
      ...prev,
      social_links: prev.social_links.map((link, i) => 
        i === index ? { ...link, [field]: value } : link
      )
    }));
  };

  const addSocialLink = () => {
    setContactData(prev => ({
      ...prev,
      social_links: [...prev.social_links, { name: '', icon: 'Users', url: '', color: 'hover:text-gray-600' }]
    }));
  };

  const removeSocialLink = (index) => {
    if (window.confirm('Are you sure you want to delete this social link? This action cannot be undone.')) {
      setContactData(prev => ({
        ...prev,
        social_links: prev.social_links.filter((_, i) => i !== index)
      }));
    }
  };

  const handleFAQChange = (index, field, value) => {
    setContactData(prev => ({
      ...prev,
      faq_items: prev.faq_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addFAQ = () => {
    setContactData(prev => ({
      ...prev,
      faq_items: [...prev.faq_items, { question: '', answer: '' }]
    }));
  };

  const removeFAQ = (index) => {
    if (window.confirm('Are you sure you want to delete this FAQ item? This action cannot be undone.')) {
      setContactData(prev => ({
        ...prev,
        faq_items: prev.faq_items.filter((_, i) => i !== index)
      }));
    }
  };

  // Contact Options Management
  const handleContactOptionChange = (index, field, value) => {
    setContactData(prev => ({
      ...prev,
      contact_options: prev.contact_options.map((option, i) => 
        i === index ? { ...option, [field]: value } : option
      )
    }));
  };

  const addContactOption = () => {
    const newId = Math.max(...contactData.contact_options.map(opt => opt.id), 0) + 1;
    setContactData(prev => ({
      ...prev,
      contact_options: [...prev.contact_options, {
        id: newId,
        type: 'custom',
        icon: 'Users',
        icon_background: 'from-gray-600 to-gray-700',
        title: 'New Contact',
        description: 'Contact description',
        value: 'Contact value',
        link: '#'
      }]
    }));
  };

  const removeContactOption = (index) => {
    if (window.confirm('Are you sure you want to delete this contact option? This action cannot be undone.')) {
      setContactData(prev => ({
        ...prev,
        contact_options: prev.contact_options.filter((_, i) => i !== index)
      }));
    }
  };

  const handleOtherContactOptionChange = (index, field, value) => {
    const updatedOptions = [...contactData.other_contact_options];
    updatedOptions[index] = {
      ...updatedOptions[index],
      [field]: value
    };
    setContactData({
      ...contactData,
      other_contact_options: updatedOptions
    });
  };

  const addOtherContactOption = () => {
    const newOption = {
      id: Date.now(),
      icon: 'Phone',
      title: '',
      description: '',
      value: ''
    };
    setContactData({
      ...contactData,
      other_contact_options: [...contactData.other_contact_options, newOption]
    });
  };

  const removeOtherContactOption = (index) => {
    if (window.confirm('Are you sure you want to delete this contact option? This action cannot be undone.')) {
      const updatedOptions = [...contactData.other_contact_options];
      updatedOptions.splice(index, 1);
      setContactData({
        ...contactData,
        other_contact_options: updatedOptions
      });
    }
  };

  const handleOfficeItemChange = (index, field, value) => {
    const updatedItems = [...contactData.office_items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    setContactData(prev => ({
      ...prev,
      office_items: updatedItems
    }));
  };

  const addOfficeItem = () => {
    const newItem = {
      id: Date.now(),
      type: 'custom',
      icon: 'MapPin',
      title: 'New Item',
      value: '',
      link: ''
    };
    setContactData(prev => ({
      ...prev,
      office_items: [...prev.office_items, newItem]
    }));
  };

  const removeOfficeItem = (index) => {
    if (window.confirm('Are you sure you want to delete this office item? This action cannot be undone.')) {
      const updatedItems = contactData.office_items.filter((_, i) => i !== index);
      setContactData(prev => ({
        ...prev,
        office_items: updatedItems
      }));
    }
  };

  const renderTopSection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Top Section</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Main Title
          </label>
          <input
            type="text"
            value={contactData.main_title}
            onChange={(e) => handleInputChange('main_title', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Get In"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Gradient Title
          </label>
          <input
            type="text"
            value={contactData.gradient_title}
            onChange={(e) => handleInputChange('gradient_title', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Touch"
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description
        </label>
        <textarea
          value={contactData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          rows="3"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Ready to start your project? Let's discuss how we can help bring your vision to life."
        />
      </div>
    </div>
  );

  const renderContactsSection = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Contact Options</h3>
        <button
          onClick={addContactOption}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Contact Option
        </button>
      </div>
      
      <div className="space-y-6">
        {contactData.contact_options.map((option, index) => (
          <div key={option.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium text-gray-900 dark:text-white">
                Contact Option {index + 1} - {option.title}
              </h4>
              <button
                onClick={() => removeContactOption(index)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Contact Type
                </label>
                <select
                  value={option.type}
                  onChange={(e) => handleContactOptionChange(index, 'type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="call">Call</option>
                  <option value="email">Email</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="schedule">Schedule Meeting</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Icon
                </label>
                <select
                  value={option.icon}
                  onChange={(e) => handleContactOptionChange(index, 'icon', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {availableIcons.map(icon => (
                    <option key={icon.name} value={icon.name}>
                      {icon.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Icon Background
                </label>
                <select
                  value={option.icon_background}
                  onChange={(e) => handleContactOptionChange(index, 'icon_background', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="from-green-600 to-green-700">Green</option>
                  <option value="from-blue-600 to-blue-700">Blue</option>
                  <option value="from-purple-600 to-purple-700">Purple</option>
                  <option value="from-red-600 to-red-700">Red</option>
                  <option value="from-yellow-600 to-yellow-700">Yellow</option>
                  <option value="from-pink-600 to-pink-700">Pink</option>
                  <option value="from-gray-600 to-gray-700">Gray</option>
                  <option value="from-indigo-600 to-indigo-700">Indigo</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={option.title}
                  onChange={(e) => handleContactOptionChange(index, 'title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Call Us"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Value
                </label>
                <input
                  type="text"
                  value={option.value}
                  onChange={(e) => handleContactOptionChange(index, 'value', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="9876543210"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Link
                </label>
                <input
                  type="text"
                  value={option.link}
                  onChange={(e) => handleContactOptionChange(index, 'link', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="tel:+919876543210"
                />
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <input
                type="text"
                value={option.description}
                onChange={(e) => handleContactOptionChange(index, 'description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Speak directly with our team"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderFormSection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Form Section</h3>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Form Title
        </label>
        <input
          type="text"
          value={contactData.form_title}
          onChange={(e) => handleInputChange('form_title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Send Us a Message"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Form Description
        </label>
        <textarea
          value={contactData.form_description}
          onChange={(e) => handleInputChange('form_description', e.target.value)}
          rows="3"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Fill out the form below and we'll get back to you within 24 hours."
        />
      </div>
    </div>
  );

  const renderOtherContactSection = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Other Ways to Reach Us</h3>
        <button
          onClick={addOtherContactOption}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Contact Option
        </button>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Section Title
        </label>
        <input
          type="text"
          value={contactData.other_contact_title}
          onChange={(e) => handleInputChange('other_contact_title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Other Ways to Reach Us"
        />
      </div>

      <div className="space-y-4">
        {contactData.other_contact_options?.map((option, index) => (
          <div key={option.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex justify-between items-start mb-4">
              <h4 className="text-md font-medium text-gray-900 dark:text-white">
                Contact Option {index + 1}
              </h4>
              <button
                onClick={() => removeOtherContactOption(index)}
                className="text-red-600 hover:text-red-700 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Icon
                </label>
                <select
                  value={option.icon}
                  onChange={(e) => handleOtherContactOptionChange(index, 'icon', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {availableIcons.map(icon => (
                    <option key={icon.name} value={icon.name}>
                      {icon.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={option.title}
                  onChange={(e) => handleOtherContactOptionChange(index, 'title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Call Us"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <input
                  type="text"
                  value={option.description}
                  onChange={(e) => handleOtherContactOptionChange(index, 'description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Give us a call"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Value
                </label>
                <input
                  type="text"
                  value={option.value}
                  onChange={(e) => handleOtherContactOptionChange(index, 'value', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="+91 9876543210"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderOfficeSection = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Office Information</h3>
        <button
          onClick={addOfficeItem}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Office Item
        </button>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Office Title
        </label>
        <input
          type="text"
          value={contactData.office_title}
          onChange={(e) => handleInputChange('office_title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Visit Our Office"
        />
      </div>
      
      <div className="space-y-6">
        {contactData.office_items.map((item, index) => (
          <div key={item.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium text-gray-900 dark:text-white">
                Office Item {index + 1} - {item.title}
              </h4>
              <button
                onClick={() => removeOfficeItem(index)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Item Type
                </label>
                <select
                  value={item.type}
                  onChange={(e) => handleOfficeItemChange(index, 'type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="address">Address</option>
                  <option value="hours">Business Hours</option>
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Icon
                </label>
                <select
                  value={item.icon}
                  onChange={(e) => handleOfficeItemChange(index, 'icon', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {availableIcons.map(icon => (
                    <option key={icon.name} value={icon.name}>
                      {icon.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={item.title}
                  onChange={(e) => handleOfficeItemChange(index, 'title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Address"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Link (Optional)
                </label>
                <input
                  type="text"
                  value={item.link}
                  onChange={(e) => handleOfficeItemChange(index, 'link', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://maps.google.com/..."
                />
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Value/Description
              </label>
              <textarea
                value={item.value}
                onChange={(e) => handleOfficeItemChange(index, 'value', e.target.value)}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="123 Business District, Andheri East, Mumbai, Maharashtra 400069"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderMapSection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Map Section</h3>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Map Title
        </label>
        <input
          type="text"
          value={contactData.map_title}
          onChange={(e) => handleInputChange('map_title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Interactive Map"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Map Address
        </label>
        <input
          type="text"
          value={contactData.map_address}
          onChange={(e) => handleInputChange('map_address', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Mumbai, Maharashtra"
        />
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Enter the address to be displayed on the map
        </p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Google Maps API Key
        </label>
        <input
          type="text"
          value={contactData.google_maps_api_key}
          onChange={(e) => handleInputChange('google_maps_api_key', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="AIzaSyB..."
        />
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Enter your Google Maps API key to enable interactive maps
        </p>
      </div>

      {/* Map Contact Info Section */}
      <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
        <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Map Contact Information</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          These contact details will be displayed as action buttons on the map
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Phone Number for Map
            </label>
            <input
              type="text"
              value={contactData.map_phone || ''}
              onChange={(e) => handleInputChange('map_phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="+91 98765 43210"
            />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Phone number shown on map call button
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email for Map
            </label>
            <input
              type="email"
              value={contactData.map_email || ''}
              onChange={(e) => handleInputChange('map_email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="hello@niswainfotech.com"
            />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Email address shown on map email button
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSocialSection = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Social Media</h3>
        <button
          onClick={addSocialLink}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Social Link
        </button>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Social Title
        </label>
        <input
          type="text"
          value={contactData.social_title}
          onChange={(e) => handleInputChange('social_title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Follow Us"
        />
      </div>
      
      <div className="space-y-4">
        {contactData.social_links.map((link, index) => (
          <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium text-gray-900 dark:text-white">Social Link {index + 1}</h4>
              <button
                onClick={() => removeSocialLink(index)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={link.name}
                  onChange={(e) => handleSocialLinkChange(index, 'name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Facebook"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Icon
                </label>
                <select
                  value={link.icon}
                  onChange={(e) => handleSocialLinkChange(index, 'icon', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {availableIcons.map(icon => (
                    <option key={icon.name} value={icon.name}>
                      {icon.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  URL
                </label>
                <input
                  type="text"
                  value={link.url}
                  onChange={(e) => handleSocialLinkChange(index, 'url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://facebook.com/..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Color
                </label>
                <select
                  value={link.color}
                  onChange={(e) => handleSocialLinkChange(index, 'color', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="hover:text-blue-600">Blue</option>
                  <option value="hover:text-red-600">Red</option>
                  <option value="hover:text-green-600">Green</option>
                  <option value="hover:text-yellow-600">Yellow</option>
                  <option value="hover:text-purple-600">Purple</option>
                  <option value="hover:text-pink-600">Pink</option>
                  <option value="hover:text-gray-600">Gray</option>
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderFAQSection = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">FAQ Section</h3>
        <button
          onClick={addFAQ}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add FAQ
        </button>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          FAQ Title
        </label>
        <input
          type="text"
          value={contactData.faq_title}
          onChange={(e) => handleInputChange('faq_title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Frequently Asked Questions"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          FAQ Description
        </label>
        <textarea
          value={contactData.faq_description}
          onChange={(e) => handleInputChange('faq_description', e.target.value)}
          rows="3"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Quick answers to common questions about our services and process"
        />
      </div>
      
      <div className="space-y-4">
        {contactData.faq_items.map((faq, index) => (
          <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium text-gray-900 dark:text-white">FAQ {index + 1}</h4>
              <button
                onClick={() => removeFAQ(index)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Question
                </label>
                <input
                  type="text"
                  value={faq.question}
                  onChange={(e) => handleFAQChange(index, 'question', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="How long does a typical project take?"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Answer
                </label>
                <textarea
                  value={faq.answer}
                  onChange={(e) => handleFAQChange(index, 'answer', e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Project timelines vary based on complexity..."
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'top-section':
        return renderTopSection();
      case 'contacts-section':
        return renderContactsSection();
      case 'form-section':
        return renderFormSection();
      case 'other-contact-section':
        return renderOtherContactSection();
      case 'office-section':
        return renderOfficeSection();
      case 'map-section':
        return renderMapSection();
      case 'social-section':
        return renderSocialSection();
      case 'faq-section':
        return renderFAQSection();
      default:
        return renderTopSection();
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          Contact Page Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Manage all content sections of the Contact Us page
        </p>
      </div>

      {/* Messages */}
      {(error || success) && (
        <div className={`p-4 rounded-lg mb-6 ${
          success 
            ? 'bg-green-100 text-green-700 border border-green-200' 
            : 'bg-red-100 text-red-700 border border-red-200'
        }`}>
          <div className="flex justify-between items-center">
            <span>{success || error}</span>
            <button onClick={clearMessage} className="text-gray-500 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Sections</h3>
            <div className="space-y-2">
              {sections.map((section) => {
                const IconComponent = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      activeSection === section.id
                        ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    {section.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
            {renderContent()}
            
            {/* Save Button */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={saveContactData}
                disabled={saving}
                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPageManagement; 